import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

const formatCurrency = (value) => {
  if (value === undefined || value === null) return '£0.00';
  
  return new Intl.NumberFormat('en-GB', { 
    style: 'currency', 
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2 
  }).format(value);
};

export const getColumnDefinitions = (isRSUIncluded, isAnnual) => {
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-GB', { 
      style: 'currency', 
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2 
    }).format(value);
  };

  const getTooltipContent = (record, dataIndex) => {
    // Implement detailed calculation explanations here
    switch (dataIndex) {
      case 'employee_pension_amount':
        return `Employee Pension Contribution = Base Salary * Employee Pension Contribution %
                = ${formatNumber(isAnnual ? record.base_salary : record.base_salary / 12)} * ${record.employee_pension_contribution_pct}%
                = ${formatNumber(isAnnual ? record.employee_pension_amount : record.employee_pension_amount / 12)}`;
      case 'gross_pay':
        return `Gross Pay = Base Salary + Bonus + RSUs
                = ${formatNumber(isAnnual ? record.base_salary : record.base_salary / 12)} + ${formatNumber(isAnnual ? record.bonus : record.bonus / 12)} + ${formatNumber(isAnnual ? record.rsus : record.rsus / 12)}
                = ${formatNumber(isAnnual ? record.gross_pay : record.gross_pay / 12)}`;
      case 'taxable_income_less_pension':
        return `Gross Pay = Base Salary + Bonus + RSUs (less NIC contribution) - Pension
                = ${formatNumber(isAnnual ? record.base_salary : record.base_salary / 12)} + ${formatNumber(isAnnual ? record.bonus : record.bonus / 12)} + ${formatNumber(isAnnual ? record.rsus_subject_to_income_tax : record.rsus_subject_to_income_tax / 12)} - ${formatNumber(isAnnual ? record.employee_pension_amount : record.employee_pension_amount / 12)}
                = ${formatNumber(isAnnual ? record.taxable_income_less_pension : record.taxable_income_less_pension / 12)}`;   
      case 'net_salary':
        return `Net Salary = Taxable Income - Total Tax - Total NIC 
                = ${formatNumber(isAnnual ? record.taxable_income_less_pension : record.taxable_income_less_pension / 12)} - ${formatNumber(isAnnual ? record.total_tax : record.total_tax / 12)} - ${formatNumber(isAnnual ? record.total_nic : record.total_nic / 12)}
                = ${formatNumber(isAnnual ? record.net_salary : record.net_salary / 12)}`;
      case 'net_salary_inc_pension':
        return `Net Salary incl Pension = Net Salary + Total Pension (Employer + Employee) Contributions 
                = ${formatNumber(isAnnual ? record.net_salary : record.net_salary / 12)} + ${formatNumber(isAnnual ? record.employee_pension_amount : record.employee_pension_amount / 12)} + ${formatNumber(isAnnual ? record.employer_pension_amount : record.employer_pension_amount / 12)}
                = ${formatNumber(isAnnual ? record.net_salary_inc_pension : record.net_salary_inc_pension / 12)}`;
      case 'gross_pay_ex_rsu':
        return `Gross Pay excl RSU = Base Salary + Bonus
                = ${formatNumber(isAnnual ? record.base_salary : record.base_salary / 12)} + ${formatNumber(isAnnual ? record.bonus : record.bonus / 12)}
                = ${formatNumber(isAnnual ? record.gross_pay_ex_rsu : record.gross_pay_ex_rsu / 12)}`;
      case 'taxable_income_less_pension_excl_rsus':
        return `Gross Pay = Base Salary + Bonus - Pension
                = ${formatNumber(isAnnual ? record.base_salary : record.base_salary / 12)} + ${formatNumber(isAnnual ? record.bonus : record.bonus / 12)} - ${formatNumber(isAnnual ? record.employee_pension_amount : record.employee_pension_amount / 12)}
                = ${formatNumber(isAnnual ? record.taxable_income_less_pension_excl_rsus : record.taxable_income_less_pension_excl_rsus / 12)}`;   
      case 'net_salary_excl_rsu':
        return `Net Salary excl RSU = (All excl RSU:) Taxable Income - Total Tax - Total NIC 
                = ${formatNumber(isAnnual ? record.taxable_income_less_pension_excl_rsus : record.taxable_income_less_pension_excl_rsus / 12)} - ${formatNumber(isAnnual ? record.total_tax_excl_rsus : record.total_tax_excl_rsus / 12)} - ${formatNumber(isAnnual ? record.total_nic_excl_rsus : record.total_nic_excl_rsus / 12)}
                = ${formatNumber(isAnnual ? record.net_salary_excl_rsu : record.net_salary_excl_rsu / 12)}`;
      case 'net_salary_inc_pension_ex_RSU':
        return `Net Salary incl Pension excl RSU = Net Salary excl RSU + Total Pension (Employer + Employee) Contributions 
                = ${formatNumber(isAnnual ? record.net_salary_excl_rsu : record.net_salary_excl_rsu / 12)} + ${formatNumber(isAnnual ? record.employee_pension_amount : record.employee_pension_amount / 12)} + ${formatNumber(isAnnual ? record.employer_pension_amount : record.employer_pension_amount / 12)}
                = ${formatNumber(isAnnual ? record.net_salary_inc_pension_ex_RSU : record.net_salary_inc_pension_ex_RSU / 12)}`;
      // Add cases for other columns...
      default:
        return 'Calculation details not available';
    }
  };

  const baseColumns = [
    {
      title: () => (
        <span>
          Employee Pension Contribution % 
          <Tooltip title="Percentage of salary contributed to pension by the employee">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'employee_pension_contribution_pct',
      key: 'employee_pension_contribution_pct',
      width: 200,
      render: (text) => `${parseFloat(text).toFixed(1)}%`,
    },
    {
      title: () => (
        <span>
          Employer Pension Contribution % 
          <Tooltip title="Percentage of salary contributed to pension by the employer">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'employer_pension_contribution_pct',
      key: 'employer_pension_contribution_pct',
      width: 200,
      render: (text) => `${text}%`,
    },
    {
      title: 'Employer Pension Contribution Amount',
      dataIndex: 'employer_pension_amount',
      key: 'employer_pension_amount',
      className: 'hidden-column', // This class will be used to hide the column
      render: (text, record) => formatNumber(isAnnual ? text : text / 12),
  },
    {
      title: () => (
        <span>
          Employee Pension Contribution 
          <Tooltip title="Amount contributed to pension by the employee">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'employee_pension_amount',
      key: 'employee_pension_amount',
      width: 200,
      render: (text, record) => (
        <Tooltip title={getTooltipContent(record, 'employee_pension_amount')}>
          <span>{formatNumber(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
  ];

  const rsuIncludedColumns = [
    {
      title: () => (
        <span>
          Gross Pay 
          <Tooltip title="Total pay including base salary, bonus, and RSUs">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'gross_pay',
      key: 'gross_pay',
      width: 150,
      render: (text, record) => (
        <Tooltip title={getTooltipContent(record, 'gross_pay')}>
          <span>{formatNumber(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
    {
        title: 'RSUs Subject to Income Tax',
        dataIndex: 'rsus_subject_to_income_tax',
        key: 'rsus_subject_to_income_tax',
        className: 'hidden-column', // This class will be used to hide the column
        render: (text, record) => formatNumber(isAnnual ? text : text / 12),
    },
    {
      title: () => (
        <span>
          Taxable Income 
          <Tooltip title="Income subject to tax after pension contributions and RSU NIC contributions (if applicable)">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'taxable_income_less_pension',
      key: 'taxable_income_less_pension',
      width: 150,
      render: (text, record) => (
        <Tooltip title={getTooltipContent(record, 'taxable_income_less_pension')}>
          <span>{formatNumber(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <span>
          Personal Allowance Reduction 
          <Tooltip title="Reduction in tax-free allowance for high earners">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'personal_allowance_reduction',
      key: 'personal_allowance_reduction',
      width: 200,
      render: (text, record) => (
        <Tooltip title={getTooltipContent(record, 'personal_allowance_reduction')}>
          <span>{formatNumber(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <span>
          Total Tax 
          <Tooltip title="Total income tax payable">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'total_tax',
      key: 'total_tax',
      width: 150,
      render: (text, record) => (
        <Tooltip title={getTooltipContent(record, 'total_tax')}>
          <span>{formatNumber(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <span>
          Total NIC 
          <Tooltip title="Total National Insurance Contributions">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'total_nic',
      key: 'total_nic',
      width: 150,
      render: (text, record) => (
        <Tooltip title={getTooltipContent(record, 'total_nic')}>
          <span>{formatNumber(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <span>
          RSU Tax Amount 
          <Tooltip title="Portion of total income tax attributable to RSU income">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'rsu_tax',
      key: 'rsu_tax',
      width: 150,
      render: (text, record) => (
        <Tooltip title="Income tax attributable to RSU income">
          <span>{formatNumber(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <span>
          Non-RSU Tax Amount
          <Tooltip title="Portion of total income tax attributable to base salary, bonus and other income">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'non_rsu_tax',
      key: 'non_rsu_tax',
      width: 150,
      render: (text, record) => (
        <Tooltip title="Income tax attributable to non-RSU income sources">
          <span>{formatNumber(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <span>
          RSU Tax %
          <Tooltip title="Percentage of total income tax attributable to RSUs">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      key: 'rsu_tax_percentage',
      width: 120,
      render: (_, record) => {
        const rsuTax = parseFloat(record.rsu_tax);
        const totalTax = parseFloat(record.total_tax);
        const percentage = totalTax > 0 ? (rsuTax / totalTax) * 100 : 0;
        return `${percentage.toFixed(1)}%`;
      },
    },
    {
      title: () => (
        <span>
          Student Loan Repayments 
          <Tooltip title="Total student loan repayments on your income">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'student_loan_repayments',
      key: 'student_loan_repayments',
      width: 200,
      render: (text, record) => (
        <Tooltip title="Student loan repayments based on your total income">
          <span>{formatCurrency(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <span>
          Net Salary 
          <Tooltip title="Take-home pay after all deductions">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'net_salary',
      key: 'net_salary',
      width: 150,
      render: (text, record) => (
        <Tooltip title={getTooltipContent(record, 'net_salary')}>
          <span>{formatNumber(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <span>
          Net Salary (incl. Total Pension) 
          <Tooltip title="Take-home pay plus total pension contributions (employer and employee)">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'net_salary_inc_pension',
      key: 'net_salary_inc_pension',
      width: 250,
      render: (text, record) => (
        <Tooltip title={getTooltipContent(record, 'net_salary_inc_pension')}>
          <span>{formatNumber(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
  ];

  const rsuExcludedColumns = [
    {
      title: () => (
        <span>
          Gross Pay (excl. RSU) 
          <Tooltip title="Total pay including base salary and bonus, excluding RSUs">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'gross_pay_ex_rsu',
      key: 'gross_pay_ex_rsu',
      width: 200,
      render: (text, record) => (
        <Tooltip title={getTooltipContent(record, 'gross_pay_ex_rsu')}>
          <span>{formatNumber(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <span>
          Taxable Income (excl. RSU) 
          <Tooltip title="Income subject to tax after pension contributions, excluding RSUs">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'taxable_income_less_pension_excl_rsus',
      key: 'taxable_income_less_pension_excl_rsus',
      width: 250,
      render: (text, record) => (
        <Tooltip title={getTooltipContent(record, 'taxable_income_less_pension_excl_rsus')}>
          <span>{formatNumber(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <span>
          Personal Allowance Reduction (excl. RSU) 
          <Tooltip title="Reduction in tax-free allowance for high earners, excluding RSUs">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'personal_allowance_reduction_excl_rsus',
      key: 'personal_allowance_reduction_excl_rsus',
      width: 300,
      render: (text, record) => (
        <Tooltip title={getTooltipContent(record, 'personal_allowance_reduction_excl_rsus')}>
          <span>{formatNumber(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <span>
          Total Tax (excl. RSU) 
          <Tooltip title="Total income tax payable, excluding RSUs">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'total_tax_excl_rsus',
      key: 'total_tax_excl_rsus',
      width: 200,
      render: (text, record) => (
        <Tooltip title={getTooltipContent(record, 'total_tax_excl_rsus')}>
          <span>{formatNumber(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <span>
          Total NIC (excl. RSU) 
          <Tooltip title="Total National Insurance Contributions, excluding RSUs">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'total_nic_excl_rsus',
      key: 'total_nic_excl_rsus',
      width: 200,
      render: (text, record) => (
        <Tooltip title={getTooltipContent(record, 'total_nic_excl_rsus')}>
          <span>{formatNumber(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <span>
          RSU Tax Amount 
          <Tooltip title="Portion of total income tax attributable to RSU income (zero when excluding RSUs)">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'rsu_tax_excl_rsus',
      key: 'rsu_tax_excl_rsus',
      width: 150,
      render: (text, record) => (
        <Tooltip title="Income tax attributable to RSU income (always zero when excluding RSUs)">
          <span>{formatNumber(isAnnual ? 0 : 0)}</span>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <span>
          Non-RSU Tax Amount
          <Tooltip title="Total income tax (same as Total Tax when excluding RSUs)">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'non_rsu_tax_excl_rsus',
      key: 'non_rsu_tax_excl_rsus',
      width: 150,
      render: (text, record) => (
        <Tooltip title="Total income tax when excluding RSUs">
          <span>{formatNumber(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <span>
          RSU Tax %
          <Tooltip title="Percentage of total income tax attributable to RSUs (always 0% when excluding RSUs)">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      key: 'rsu_tax_percentage_excl_rsus',
      width: 120,
      render: () => "0.0%",
    },
    {
      title: () => (
        <span>
          Student Loan Repayments (excl. RSU) 
          <Tooltip title="Student loan repayments excluding RSU income">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'student_loan_repayments_excl_rsus',
      key: 'student_loan_repayments_excl_rsus',
      width: 250,
      render: (text, record) => (
        <Tooltip title="Student loan repayments excluding RSU income">
          <span>{formatCurrency(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <span>
          Net Salary (excl. RSU) 
          <Tooltip title="Take-home pay after all deductions, excluding RSUs">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'net_salary_excl_rsu',
      key: 'net_salary_excl_rsu',
      width: 200,
      render: (text, record) => (
        <Tooltip title={getTooltipContent(record, 'net_salary_excl_rsu')}>
          <span>{formatNumber(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <span>
          Net Salary (incl. Total Pension, excl. RSU) 
          <Tooltip title="Take-home pay plus total pension contributions (employer and employee), excluding RSUs">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'net_salary_inc_pension_ex_RSU',
      key: 'net_salary_inc_pension_ex_RSU',
      width: 350,
      render: (text, record) => (
        <Tooltip title={getTooltipContent(record, 'net_salary_inc_pension_ex_RSU')}>
          <span>{formatNumber(isAnnual ? text : text / 12)}</span>
        </Tooltip>
      ),
    },
  ];

  return [
    ...baseColumns,
    ...(isRSUIncluded ? rsuIncludedColumns : rsuExcludedColumns),
  ];
};

export default getColumnDefinitions;